import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ClientsEnhanced.css";
import axios from "axios";
import EditClient from "./EditClient";
import { Eye, Edit, User, X, AlertCircle, Download, Filter, Mail } from "lucide-react";
import { toast } from "react-toastify";

const LIST_URL = "https://server.tradefxonline.com/getClients.php";
const CREATE_URL = "https://server.tradefxonline.com/createnew.php";
const EDIT_URL = "https://server.tradefxonline.com/admin_editusers.php";
const VIEW_URL = "https://server.tradefxonline.com/admin_viewusers.php";
const EMAIL_VERIFY_URL = "https://server.tradefxonline.com/admin_verify_email.php";
const KYC_VERIFY_UNVERIFY_URL = "https://server.tradefxonline.com/admin_kyc_verify_unverify.php";
const EMAIL_VERIFY_UNVERIFY_URL = "https://server.tradefxonline.com/admin_email_verify_unverify.php";
const DELETE_CLIENT_URL = "https://server.tradefxonline.com/admin_delete_client.php";
const BLOCK_CLIENT_URL = "https://server.tradefxonline.com/admin_block_client_capital.php";

// Country flag component using REST Countries API
const CountryFlag = ({ country, className = "" }) => {
  const [flagUrl, setFlagUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!country) {
      setLoading(false);
      return;
    }

    // Use REST Countries API for flag images
    const fetchFlag = async () => {
      try {
        const response = await fetch(`https://restcountries.com/v3.1/name/${country}?fields=flags`);
        const data = await response.json();
        if (data && data[0] && data[0].flags && data[0].flags.svg) {
          setFlagUrl(data[0].flags.svg);
        }
      } catch (error) {
        console.error('Error fetching flag:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlag();
  }, [country]);

  if (loading) {
    return <div className={`flag-placeholder ${className}`}></div>;
  }

  if (!flagUrl) {
    return <div className={`flag-placeholder ${className}`}>{country?.charAt(0)}</div>;
  }

  return (
    <img 
      src={flagUrl} 
      alt={`${country} flag`} 
      className={`country-flag ${className}`}
      onError={() => setFlagUrl("")}
    />
  );
};

// Status badge component
const StatusBadge = ({ status, type }) => {
  const getStatusClass = () => {
    if (type === 'account') {
      return status?.toLowerCase() === 'active' ? 'status-active' : 'status-inactive';
    }
    if (type === 'kyc') {
      // Handle numeric KYC status: 2 = Verified, anything else = Not Verified
      if (status === "2" || status === 2) {
        return 'status-verified';
      } else {
        return 'status-pending';
      }
    }
    if (type === 'email') {
      // Handle Email KYC status: 1 = Verified, 0 = Not Verified
      if (status === "1" || status === 1) {
        return 'status-verified';
      } else {
        return 'status-pending';
      }
    }
    if (type === 'user') {
      return status?.toLowerCase() === 'enabled' ? 'status-verified' : 'status-pending';
    }
    return 'status-pending';
  };

  const getDisplayText = () => {
    if (type === 'kyc') {
      // Convert numeric KYC status to readable text: 2 = Verified
      if (status === "2" || status === 2) {
        return 'Verified';
      } else {
        return 'Not Verified';
      }
    }
    if (type === 'email') {
      // Convert Email KYC status: 1 = Verified, 0 = Not Verified
      if (status === "1" || status === 1) {
        return 'Verified';
      } else {
        return 'Not Verified';
      }
    }
    return status || 'N/A';
  };

  return (
    <span className={`status-badge ${getStatusClass()}`}>
      {getDisplayText()}
    </span>
  );
};

// Reusable Modal
function Modal({ open, title, onClose, error, children, footer }) {
  if (!open) return null;
  return (
    <div className="enhanced-modal__overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="enhanced-modal__card">
        <div className="enhanced-modal__header">
          <h3 className="enhanced-modal__title">{title}</h3>
          <button className="enhanced-modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="enhanced-modal__error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="enhanced-modal__body">{children}</div>

        {footer && <div className="enhanced-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

function ClientsEnhanced() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([
    // Sample data matching the new API structure
    {
      id: "182",
      trade_id: "N/A",
      account_name: "anna petrova",
      email: "an023.petrova@gmail.com",
      phone_pwd: "89186901983",
      strategycountry: "Россия",
      status: "Enabled",
      email_kyc: "1",
      password: "Qazxcv@23",
      kyc_status: "0"
    },
    {
      id: "181",
      trade_id: "N/A",
      account_name: "COPY TEST 4",
      email: "katariyadivu88200@gmail.com",
      phone_pwd: "8200492728",
      strategycountry: "India",
      status: "Enabled",
      email_kyc: "1",
      password: "123456",
      kyc_status: "1"
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [filteredClients, setFilteredClients] = useState(clients);
  const [emailVerifyLoading, setEmailVerifyLoading] = useState({});
  const [kycVerifyLoading, setKycVerifyLoading] = useState({});
  const [deleteLoading, setDeleteLoading] = useState({});
  const [blockLoading, setBlockLoading] = useState({});

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    emailKyc: "All",
    kycStatus: "All",
    startDate: "",
    endDate: ""
  });

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: "", last_name: "", country: "", phone: "", dob: "", email: "", password: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Keep selectedClient for compatibility but remove unused edit modal states
  const [selectedClient, setSelectedClient] = useState(null);



  // Additional modal states
    const [showAddModal, setShowAddModal] = useState(false);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await axios.get(LIST_URL);
      if (res.data?.success) {
        setClients(res.data.clients || []);
        setFilteredClients(res.data.clients || []);
      }
    } catch (e) {
      console.error("Error fetching clients:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(); // Uncomment to use real API
    setFilteredClients(clients); // Use sample data for now
  }, []);

  // Filter functionality
  useEffect(() => {
    let filtered = [...clients];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(client => 
        client.account_name?.toLowerCase().includes(searchTerm) ||
        client.email?.toLowerCase().includes(searchTerm) ||
        client.phone_pwd?.toLowerCase().includes(searchTerm) ||
        client.strategycountry?.toLowerCase().includes(searchTerm)
      );
    }

    // Email KYC filter
    if (filters.emailKyc !== "All") {
      filtered = filtered.filter(client => {
        const emailKycValue = filters.emailKyc === "true" ? "1" : "0";
        return client.email_kyc === emailKycValue;
      });
    }

    // KYC Status filter
    if (filters.kycStatus !== "All") {
      filtered = filtered.filter(client => {
        if (filters.kycStatus === "true") {
          // Verified = status 2
          return client.kyc_status === "2" || client.kyc_status === 2;
        } else {
          // Not Verified = anything other than 2
          return client.kyc_status !== "2" && client.kyc_status !== 2;
        }
      });
    }

    // Date filter
    if (filters.startDate || filters.endDate) {
      filtered = filtered.filter(client => {
        const createdDate = new Date(client.created_at);
        const regDate = new Date(client.reg_date);
        
        let matchesDateRange = true;
        
        if (filters.startDate) {
          const startDate = new Date(filters.startDate);
          matchesDateRange = matchesDateRange && (createdDate >= startDate || regDate >= startDate);
        }
        
        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999); // Include the entire end date
          matchesDateRange = matchesDateRange && (createdDate <= endDate || regDate <= endDate);
        }
        
        return matchesDateRange;
      });
    }

    setFilteredClients(filtered);
  }, [filters, clients]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      emailKyc: "All",
      kycStatus: "All",
      startDate: "",
      endDate: ""
    });
  };

  const handleProfileClick = (email) => {
    const clientBaseURL = "http://localhost:8080";
    window.open(`${clientBaseURL}/login?email=${encodeURIComponent(email)}`, "_blank");
  };

  // Delete account
  const handleDeleteAccount = async (client) => {
    if (!window.confirm(`Are you sure you want to delete ${client.account_name}'s account? This action cannot be undone.`)) {
      return;
    }

    const loadingKey = `delete-${client.id}`;
    
    try {
      setDeleteLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const response = await axios.post(DELETE_CLIENT_URL, {
        email: client.email
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.success || response.data?.status === "success") {
        toast.success(response.data.message || "Account deleted successfully!");
        
        // Remove the client from the local state
        setClients(prevClients => prevClients.filter(c => c.id !== client.id));
        setFilteredClients(prevClients => prevClients.filter(c => c.id !== client.id));
      } else {
        toast.error(response.data?.message || "Failed to delete account");
      }
    } catch (error) {
      console.error("Delete account error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete account");
    } finally {
      setDeleteLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Block/Unblock account
  const handleBlockAccount = async (client, action) => {
    const actionText = action === "block" ? "block" : "unblock";
    if (!window.confirm(`Are you sure you want to ${actionText} ${client.account_name}'s account?`)) {
      return;
    }

    const loadingKey = `block-${client.id}`;
    
    try {
      setBlockLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const response = await axios.post(BLOCK_CLIENT_URL, {
        email: client.email,
        action: action
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.success || response.data?.status === "success") {
        toast.success(response.data.message || `Account ${actionText}ed successfully!`);
        
        // Update the client's trading_status locally
        const newTradingStatus = action === "block" ? "1" : "0";
        setClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, trading_status: newTradingStatus } : c
          )
        );
        setFilteredClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, trading_status: newTradingStatus } : c
          )
        );
      } else {
        toast.error(response.data?.message || `Failed to ${actionText} account`);
      }
    } catch (error) {
      console.error(`${actionText} account error:`, error);
      toast.error(error?.response?.data?.message || `Failed to ${actionText} account`);
    } finally {
      setBlockLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const splitName = (full = "") => {
    const parts = String(full).trim().split(/\s+/);
    if (parts.length <= 1) return { first: parts[0] || "", last: "" };
    return { first: parts.slice(0, -1).join(" "), last: parts.slice(-1).join(" ") };
  };

  // Create
  const openCreate = () => {
    setCreateError("");
    setCreateForm({ first_name: "", last_name: "", country: "", phone: "", dob: "", email: "", password: "" });
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreateError("");
    setCreating(true);
    try {
      const res = await axios.post(CREATE_URL, createForm, { headers: { "Content-Type": "application/json" }});
      if (res.data?.success || res.data?.status === "success") {
        setShowCreate(false);
        await fetchClients();
      } else {
        setCreateError(res.data?.message || res.data?.error || "Create failed. Check input.");
      }
    } catch (err) {
      setCreateError(err?.response?.data?.message || err.message || "Network error");
    } finally {
      setCreating(false);
    }
  };

  // Edit
  const handleEditClient = (client) => {
    // Navigate to edit page with client ID
    navigate(`/edit-client/${client.id}`);
  };



  // View
  const openView = (client) => {
    console.log('client: ', client);

    // Navigate to view page with client ID
    navigate(`/view-client/${client.id}`);
  };

  // Email verification
  const handleEmailVerify = async (client) => {
    const loadingKey = `email-${client.id}`;
    
    try {
      setEmailVerifyLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const response = await axios.post(EMAIL_VERIFY_URL, {
        email: client.email
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.status === "success") {
        toast.success(response.data.message || "Email verified successfully!");
        
        // Update the client's email_kyc status locally
        setClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, email_kyc: "1" } : c
          )
        );
        setFilteredClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, email_kyc: "1" } : c
          )
        );
      } else {
        toast.error(response.data?.message || "Email verification failed");
      }
    } catch (error) {
      console.error("Email verification error:", error);
      toast.error(error?.response?.data?.message || "Email verification failed");
    } finally {
      setEmailVerifyLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // KYC verification/unverification
  const handleKycVerification = async (client, action) => {
    const loadingKey = `kyc-${client.id}`;
    
    try {
      setKycVerifyLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const response = await axios.post(KYC_VERIFY_UNVERIFY_URL, {
        user_id: parseInt(client.id),
        action: action
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.success) {
        toast.success(response.data.message || `KYC ${action}ed successfully!`);
        
        // Update the client's kyc_status locally (2 = verified, 0 = not verified)
        const newKycStatus = action === "verify" ? "2" : "0";
        setClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, kyc_status: newKycStatus } : c
          )
        );
        setFilteredClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, kyc_status: newKycStatus } : c
          )
        );
      } else {
        toast.error(response.data?.message || `KYC ${action} failed`);
      }
    } catch (error) {
      console.error(`KYC ${action} error:`, error);
      toast.error(error?.response?.data?.message || `KYC ${action} failed`);
    } finally {
      setKycVerifyLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Email verification/unverification
  const handleEmailVerification = async (client, action) => {
    const loadingKey = `email-verify-${client.id}`;
    
    try {
      setEmailVerifyLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      const response = await axios.post(EMAIL_VERIFY_UNVERIFY_URL, {
        user_id: parseInt(client.id),
        action: action
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.success) {
        toast.success(response.data.message || `Email ${action}ed successfully!`);
        
        // Update the client's email_kyc status locally
        const newEmailKycStatus = action === "verify" ? "1" : "0";
        setClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, email_kyc: newEmailKycStatus } : c
          )
        );
        setFilteredClients(prevClients => 
          prevClients.map(c => 
            c.id === client.id ? { ...c, email_kyc: newEmailKycStatus } : c
          )
        );
      } else {
        toast.error(response.data?.message || `Email ${action} failed`);
      }
    } catch (error) {
      console.error(`Email ${action} error:`, error);
      toast.error(error?.response?.data?.message || `Email ${action} failed`);
    } finally {
      setEmailVerifyLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const exportToExcel = () => {
    // Simple CSV export functionality
    const headers = ['#', 'Trading Accounts', 'Full Name', 'Email', 'Mobile', 'Country', 'Account Status', 'KYC Status', 'User Status'];
    const csvContent = [
      headers.join(','),
      ...filteredClients.map((client, index) => [
        index + 1,
        `[${client.id}]`,
        client.account_name,
        client.email,
        client.phone_pwd,
        client.strategycountry,
        client.status,
        client.kyc_status,
        client.user_status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients_export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="enhanced-clients-container">
      {/* Page Header */}
      <div className="enhanced-page-header">
        <div className="enhanced-header-left">
          <h1 className="enhanced-page-title">Clients List</h1>
          <p className="enhanced-page-subtitle">View all clients</p>
        </div>
    
      </div>

      {/* Search and Filters */}
      <div className="enhanced-search-section">
        <div className="enhanced-search-bar">
          <input 
            type="text" 
            placeholder="Search by Client Name, Trading Account, Email, Phone, Country"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="enhanced-search-input"
          />
          <button className="enhanced-btn enhanced-btn-reset" onClick={resetFilters}>
            Reset Filters
          </button>
        </div>

        <div className="enhanced-filter-row">
          <select 
            value={filters.emailKyc} 
            onChange={(e) => handleFilterChange('emailKyc', e.target.value)}
            className="enhanced-filter-select"
          >
            <option value="All">Email KYC Status</option>
            <option value="true">Verified</option>
            <option value="false">Not Verified</option>
          </select>

          <select 
            value={filters.kycStatus} 
            onChange={(e) => handleFilterChange('kycStatus', e.target.value)}
            className="enhanced-filter-select"
          >
            <option value="All">KYC Status</option>
            <option value="true">Verified</option>
            <option value="false">Not Verified</option>
          </select>

          <input 
            type="date" 
            placeholder="Start Date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="enhanced-filter-date"
            title="Filter by Created Date or Registration Date"
          />

          <input 
            type="date" 
            placeholder="End Date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="enhanced-filter-date"
            title="Filter by Created Date or Registration Date"
          />

          <button className="enhanced-btn enhanced-btn-export" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="enhanced-table-container">
        <table className="enhanced-clients-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Trading Accounts</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Country</th>
              <th>KYC Status</th>
              <th>Password</th>
              <th>Email KYC</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="12" className="enhanced-loading">Loading…</td></tr>
            ) : filteredClients.length === 0 ? (
              <tr><td colSpan="12" className="enhanced-no-data">No data found</td></tr>
            ) : (
              filteredClients.map((client, index) => (
                <tr key={client.id} className="enhanced-table-row">
                  <td className="enhanced-cell-number">{index + 1}</td>
                  <td className="enhanced-cell-account">[{client.id}]</td>
                  <td className="enhanced-cell-name">{client.account_name}</td>
                  <td className="enhanced-cell-email">{client.email}</td>
                  <td className="enhanced-cell-phone">{client.phone_pwd}</td>
                  <td className="enhanced-cell-country">
                    <div className="enhanced-country-display">
                      <CountryFlag country={client.strategycountry} className="enhanced-flag" />
                      <span>{client.strategycountry}</span>
                    </div>
                  </td>
                
                  <td className="enhanced-cell-status">
                    <StatusBadge status={client.kyc_status} type="kyc" />
                  </td>
                  <td className="enhanced-cell-password">
                    <span className="enhanced-password-text">{client.password}</span>
                  </td>
                  <td className="enhanced-cell-status">
                    <StatusBadge status={client.email_kyc} type="email" />
                  </td>
                  <td className="enhanced-cell-date">
                    {client.created_at ? new Date(client.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'}
                  </td>
                  <td className="enhanced-cell-actions">
                    <div className="enhanced-action-buttons">
                      {/* KYC Verification Buttons */}
                      {client.kyc_status === "2" || client.kyc_status === 2 ? (
                        <button 
                          className="enhanced-action-btn enhanced-action-unverify" 
                          title="Unverify KYC" 
                          onClick={() => handleKycVerification(client, "unverify")}
                          disabled={kycVerifyLoading[`kyc-${client.id}`]}
                        >
                          {kycVerifyLoading[`kyc-${client.id}`] ? (
                            <>
                              <div className="enhanced-verify-spinner" />
                              Unverifying...
                            </>
                          ) : (
                            <>
                              <X size={16} />
                              Unverify KYC
                            </>
                          )}
                        </button>
                      ) : (
                        <button 
                          className="enhanced-action-btn enhanced-action-verify" 
                          title="Verify KYC" 
                          onClick={() => handleKycVerification(client, "verify")}
                          disabled={kycVerifyLoading[`kyc-${client.id}`]}
                        >
                          {kycVerifyLoading[`kyc-${client.id}`] ? (
                            <>
                              <div className="enhanced-verify-spinner" />
                              Verifying...
                            </>
                          ) : (
                            <>
                              <AlertCircle size={16} />
                              Verify KYC
                            </>
                          )}
                        </button>
                      )}

                      {/* Email Verification Buttons */}
                      {client.email_kyc === "1" || client.email_kyc === 1 ? (
                        <button 
                          className="enhanced-action-btn enhanced-action-unverify" 
                          title="Unverify Email" 
                          onClick={() => handleEmailVerification(client, "unverify")}
                          disabled={emailVerifyLoading[`email-verify-${client.id}`]}
                        >
                          {emailVerifyLoading[`email-verify-${client.id}`] ? (
                            <>
                              <div className="enhanced-verify-spinner" />
                              Unverifying...
                            </>
                          ) : (
                            <>
                              <X size={16} />
                              Unverify Email
                            </>
                          )}
                        </button>
                      ) : (
                        <button 
                          className="enhanced-action-btn enhanced-action-verify" 
                          title="Verify Email" 
                          onClick={() => handleEmailVerification(client, "verify")}
                          disabled={emailVerifyLoading[`email-verify-${client.id}`]}
                        >
                          {emailVerifyLoading[`email-verify-${client.id}`] ? (
                            <>
                              <div className="enhanced-verify-spinner" />
                              Verifying...
                            </>
                          ) : (
                            <>
                              <Mail size={16} />
                              Verify Email
                            </>
                          )}
                        </button>
                      )}
                   
                      {/* Block/Unblock Button */}
                      {client.trading_status === "0" || client.trading_status === 0 ? (
                        <button 
                          className="enhanced-action-btn enhanced-action-block" 
                          title="Block Account" 
                          onClick={() => handleBlockAccount(client, "block")}
                          disabled={blockLoading[`block-${client.id}`]}
                        >
                          {blockLoading[`block-${client.id}`] ? (
                            <>
                              <div className="enhanced-verify-spinner" />
                              Blocking...
                            </>
                          ) : (
                            <>
                              <X size={16} />
                              Block
                            </>
                          )}
                        </button>
                      ) : (
                        <button 
                          className="enhanced-action-btn enhanced-action-verify" 
                          title="Unblock Account" 
                          onClick={() => handleBlockAccount(client, "unblock")}
                          disabled={blockLoading[`block-${client.id}`]}
                        >
                          {blockLoading[`block-${client.id}`] ? (
                            <>
                              <div className="enhanced-verify-spinner" />
                              Unblocking...
                            </>
                          ) : (
                            <>
                              <AlertCircle size={16} />
                              Unblock
                            </>
                          )}
                        </button>
                      )}

                      {/* Delete Button */}
                      <button 
                        className="enhanced-action-btn enhanced-action-delete" 
                        title="Delete Account" 
                        onClick={() => handleDeleteAccount(client)}
                        disabled={deleteLoading[`delete-${client.id}`]}
                      >
                        {deleteLoading[`delete-${client.id}`] ? (
                          <>
                            <div className="enhanced-verify-spinner" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <X size={16} />
                            Delete
                          </>
                        )}
                      </button>

                      <button 
                        className="enhanced-action-btn enhanced-action-edit" 
                        title="Edit" 
                        onClick={() => handleEditClient(client)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Modal
        open={showCreate}
        title="Create New Client"
        onClose={() => setShowCreate(false)}
        error={createError}
        footer={
          <>
            <button className="enhanced-modal-btn enhanced-modal-btn-cancel" type="button" onClick={() => setShowCreate(false)} disabled={creating}>
              Cancel
            </button>
            <button className="enhanced-modal-btn enhanced-modal-btn-primary" type="submit" form="createClientForm" disabled={creating}>
              {creating ? "Creating…" : "Create"}
            </button>
          </>
        }
      >
        <form id="createClientForm" onSubmit={submitCreate}>
          <div className="enhanced-form-grid">
            <div className="enhanced-form-field">
              <label>First Name</label>
              <input 
                className="enhanced-form-input" 
                value={createForm.first_name} 
                onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})} 
                required 
              />
            </div>
            <div className="enhanced-form-field">
              <label>Last Name</label>
              <input 
                className="enhanced-form-input" 
                value={createForm.last_name} 
                onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})} 
                required 
              />
            </div>
            <div className="enhanced-form-field">
              <label>Country</label>
              <input 
                className="enhanced-form-input" 
                value={createForm.country} 
                onChange={(e) => setCreateForm({...createForm, country: e.target.value})} 
                required 
              />
            </div>
            <div className="enhanced-form-field">
              <label>Phone</label>
              <input 
                className="enhanced-form-input" 
                type="tel" 
                value={createForm.phone} 
                onChange={(e) => setCreateForm({...createForm, phone: e.target.value})} 
                required 
              />
            </div>
            <div className="enhanced-form-field">
              <label>Date of Birth</label>
              <input 
                className="enhanced-form-input" 
                type="date" 
                value={createForm.dob} 
                onChange={(e) => setCreateForm({...createForm, dob: e.target.value})} 
                required 
              />
            </div>
            <div className="enhanced-form-field">
              <label>Email</label>
              <input 
                className="enhanced-form-input" 
                type="email" 
                value={createForm.email} 
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})} 
                required 
              />
            </div>
            <div className="enhanced-form-field enhanced-form-field-full">
              <label>Password</label>
              <input 
                className="enhanced-form-input" 
                type="password" 
                value={createForm.password} 
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})} 
                required 
              />
            </div>
          </div>
        </form>
      </Modal>








    </div>
  );
}

export default ClientsEnhanced;